import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mountain, Send, X, Loader2 } from "lucide-react";
import { API } from "../lib/api";
import { useI18n } from "../context/I18nContext";

const SUGGESTIONS = [
  "Creami un tour dettagliato per i giorni che sono in vacanza qui",
  "Come funziona il self check-in?",
  "Cosa posso fare oggi a Dimaro?",
  "Dove noleggio gli sci con sconto?",
  "Migliori ristoranti a Dimaro?",
  "Cosa fare a Dimaro se piove?",
];

function Rich({ text }) {
  const parts = String(text || "").split(/(\*\*[^*]+\*\*)/g);
  return <>{parts.map((p, i) => (p.startsWith("**") && p.endsWith("**")
    ? <strong key={i} className="font-bold">{p.slice(2, -2)}</strong>
    : <span key={i}>{p}</span>))}</>;
}

export default function ConciergeWidget() {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Ciao! Sono il Concierge Alpino 🏔️ Per assisterti al meglio con il tuo soggiorno, **A CHE NOME È STATA EFFETTUATA LA PRENOTAZIONE?** (Se non hai ancora prenotato, posso comunque darti informazioni generali sulla zona.)" },
  ]);
  const [input, setInput] = useState("");
  const [sessionId] = useState(() => "web-" + Math.random().toString(36).slice(2));
  const [loading, setLoading] = useState(false);
  const [showHint, setShowHint] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShowHint(true), 3500);
    return () => clearTimeout(timer);
  }, []);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setLoading(true);
    try {
      const res = await fetch(`${API}/concierge/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: msg }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: acc };
          return copy;
        });
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Spiacente, si è verificato un errore. Riprova." };
        return copy;
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="fixed bottom-5 right-5 z-50 flex items-center gap-3">
        <AnimatePresence>
          {!open && showHint && (
            <motion.div
              initial={{ opacity: 0, x: 20, scale: 0.9 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 20 }}
              className="glass rounded-2xl px-4 py-3 shadow-xl max-w-[220px] hidden sm:block"
            >
              <button data-testid="ai-hint-close" onClick={() => setShowHint(false)}
                className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-fir text-ivory text-xs flex items-center justify-center">×</button>
              <p className="text-sm text-fir-deep leading-snug">
                👋 {t("Ciao! Sono il Concierge Alpino. Chiedimi info sulla casa o cosa fare in zona!")}
              </p>
            </motion.div>
          )}
        </AnimatePresence>
        <button
          data-testid="ai-chat-toggle"
          onClick={() => { setOpen((o) => !o); setShowHint(false); }}
          aria-label="Apri assistente virtuale"
          className={open
            ? "relative h-16 w-16 rounded-full bg-fir text-gold shadow-2xl flex items-center justify-center border border-wood/40 hover:scale-105 transition-transform"
            : "relative flex items-center gap-2.5 rounded-full bg-fir text-ivory pl-4 pr-5 py-3.5 shadow-2xl border border-wood/40 hover:scale-105 transition-transform"}
        >
          {!open && <span className="absolute -left-1 -top-1 h-4 w-4 rounded-full bg-gold animate-ping" />}
          {open ? (
            <X size={26} />
          ) : (
            <>
              <span className="relative flex h-9 w-9 items-center justify-center rounded-full bg-gold text-fir-deep shrink-0">
                <Mountain size={20} />
              </span>
              <span className="text-left leading-tight">
                <span className="block font-display text-base text-gold">{t("Concierge Alpino")}</span>
                <span className="block text-[11px] text-ivory/75">{t("Chiedi qui · assistenza H24")}</span>
              </span>
            </>
          )}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="ai-chat-panel"
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.96 }}
            transition={{ duration: 0.25 }}
            className="fixed bottom-24 right-5 z-50 w-[92vw] max-w-sm h-[70vh] max-h-[560px] rounded-3xl overflow-hidden shadow-2xl flex flex-col glass"
          >
            <div className="bg-fir text-ivory px-5 py-4 flex items-center gap-3">
              <Mountain className="text-gold" size={22} />
              <div>
                <p className="font-display text-xl leading-none">Concierge Alpino</p>
                <p className="text-xs text-ivory/70">{t("Assistenza H24 · multilingua")}</p>
              </div>
            </div>

            <div data-lenis-prevent className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-ivory/60">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                      m.role === "user" ? "bg-fir text-ivory rounded-br-sm" : "bg-white text-fir-deep rounded-bl-sm border border-stone"
                    }`}
                  >
                    {m.role === "assistant"
                      ? (m.content
                          ? <Rich text={i === 0 ? t(m.content) : m.content} />
                          : (loading && i === messages.length - 1 ? <Loader2 className="animate-spin" size={16} /> : ""))
                      : m.content}
                  </div>
                </div>
              ))}
              {messages.length <= 1 && (
                <div className="pt-2 flex flex-wrap gap-2">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      data-testid="ai-suggestion-btn"
                      onClick={() => send(s)}
                      className="text-xs px-3 py-1.5 rounded-full bg-white border border-wood/40 text-fir hover:bg-gold/20 transition-colors"
                    >
                      {t(s)}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="p-3 bg-white border-t border-stone flex items-center gap-2">
              <input
                data-testid="ai-chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder={t("Scrivi un messaggio...")}
                className="flex-1 px-4 py-2.5 rounded-full bg-ivory border border-stone text-sm outline-none focus:border-gold"
              />
              <button
                data-testid="ai-chat-send"
                onClick={() => send()}
                disabled={loading}
                className="h-10 w-10 rounded-full bg-gold text-fir-deep flex items-center justify-center disabled:opacity-50"
              >
                <Send size={18} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
